// src/swarm/dagEngine.ts
// Dependency Graph Builder with Topological Sort
// Implements Kahn's algorithm for DAG validation and execution ordering

import { AgentTask, DAGNode, ExecutionGraph } from './types';

export class DAGEngine {
  private nodes: Map<string, DAGNode> = new Map();
  private adjacencyList: Map<string, Set<string>> = new Map();
  private reverseAdjacency: Map<string, Set<string>> = new Map();

  constructor(tasks: AgentTask[]) {
    this.buildGraph(tasks);
  }

  private buildGraph(tasks: AgentTask[]): void {
    // Initialize nodes
    for (const task of tasks) {
      this.nodes.set(task.id, {
        id: task.id,
        task,
        dependencies: [...task.dependsOn],
        dependents: [],
        depth: 0,
      });
      this.adjacencyList.set(task.id, new Set(task.dependsOn));
      this.reverseAdjacency.set(task.id, new Set());
    }

    // Build reverse adjacency (who depends on me)
    for (const task of tasks) {
      for (const dep of task.dependsOn) {
        if (this.reverseAdjacency.has(dep)) {
          this.reverseAdjacency.get(dep)!.add(task.id);
        }
      }
    }

    // Calculate depths using BFS from roots
    this.calculateDepths();
  }

  private calculateDepths(): void {
    const inDegree = new Map<string, number>();

    // Calculate in-degrees
    for (const [id, node] of this.nodes) {
      inDegree.set(id, node.dependencies.length);
    }

    // Find root nodes (no dependencies)
    const queue: string[] = [];
    for (const [id, degree] of inDegree) {
      if (degree === 0) {
        queue.push(id);
        this.nodes.get(id)!.depth = 0;
      }
    }

    // BFS to calculate depths
    while (queue.length > 0) {
      const current = queue.shift()!;
      const currentNode = this.nodes.get(current)!;

      for (const dependent of this.reverseAdjacency.get(current) || []) {
        const dependentNode = this.nodes.get(dependent)!;
        dependentNode.depth = Math.max(dependentNode.depth, currentNode.depth + 1);

        const newDegree = (inDegree.get(dependent) || 0) - 1;
        inDegree.set(dependent, newDegree);

        if (newDegree === 0) {
          queue.push(dependent);
        }
      }
    }
  }

  /**
   * Detect cycles using DFS with color marking
   * White (0) = unvisited, Gray (1) = in progress, Black (2) = done
   */
  detectCycles(): string[][] {
    const colors = new Map<string, number>();
    const parent = new Map<string, string | null>();
    const cycles: string[][] = [];

    for (const id of this.nodes.keys()) {
      colors.set(id, 0);
      parent.set(id, null);
    }

    const dfs = (nodeId: string, path: string[]): void => {
      colors.set(nodeId, 1);

      for (const dep of this.adjacencyList.get(nodeId) || []) {
        if (colors.get(dep) === 1) {
          // Found cycle - extract it
          const cycleStart = path.indexOf(dep);
          if (cycleStart !== -1) {
            cycles.push(path.slice(cycleStart).concat([dep]));
          }
        } else if (colors.get(dep) === 0) {
          parent.set(dep, nodeId);
          dfs(dep, [...path, dep]);
        }
      }

      colors.set(nodeId, 2);
    };

    for (const id of this.nodes.keys()) {
      if (colors.get(id) === 0) {
        dfs(id, [id]);
      }
    }

    return cycles;
  }

  /**
   * Get execution layers - tasks that can run in parallel at each depth
   */
  getExecutionLayers(): string[][] {
    const maxDepth = Math.max(...Array.from(this.nodes.values()).map(n => n.depth));
    const layers: string[][] = [];

    for (let d = 0; d <= maxDepth; d++) {
      const layer = Array.from(this.nodes.values())
        .filter(n => n.depth === d)
        .map(n => n.id);
      if (layer.length > 0) {
        layers.push(layer);
      }
    }

    return layers;
  }

  /**
   * Get tasks that are ready to execute (all dependencies met)
   */
  getReadyTasks(completedTasks: Set<string>): string[] {
    const ready: string[] = [];

    for (const [id, node] of this.nodes) {
      if (completedTasks.has(id)) continue;

      const allDepsMet = node.dependencies.every(dep => completedTasks.has(dep));
      if (allDepsMet) {
        ready.push(id);
      }
    }

    return ready;
  }

  /**
   * Get the critical path (longest path from root to leaf)
   */
  getCriticalPath(): string[] {
    const memo = new Map<string, { length: number; path: string[] }>();

    const dfs = (nodeId: string): { length: number; path: string[] } => {
      if (memo.has(nodeId)) return memo.get(nodeId)!;

      const dependents = Array.from(this.reverseAdjacency.get(nodeId) || []);

      if (dependents.length === 0) {
        memo.set(nodeId, { length: 1, path: [nodeId] });
        return { length: 1, path: [nodeId] };
      }

      let maxLength = 0;
      let maxPath: string[] = [nodeId];

      for (const dep of dependents) {
        const result = dfs(dep);
        if (result.length + 1 > maxLength) {
          maxLength = result.length + 1;
          maxPath = [nodeId, ...result.path];
        }
      }

      memo.set(nodeId, { length: maxLength, path: maxPath });
      return { length: maxLength, path: maxPath };
    };

    const rootNodes = Array.from(this.nodes.values())
      .filter(n => n.dependencies.length === 0)
      .map(n => n.id);

    let maxLength = 0;
    let criticalPath: string[] = [];

    for (const root of rootNodes) {
      const result = dfs(root);
      if (result.length > maxLength) {
        maxLength = result.length;
        criticalPath = result.path;
      }
    }

    return criticalPath;
  }

  /**
   * Build the complete execution graph
   */
  buildExecutionGraph(): ExecutionGraph {
    const cycles = this.detectCycles();
    const layers = this.getExecutionLayers();
    const criticalPath = this.getCriticalPath();

    const rootNodes = Array.from(this.nodes.values())
      .filter(n => n.dependencies.length === 0)
      .map(n => n.id);

    const leafNodes = Array.from(this.nodes.values())
      .filter(n => this.reverseAdjacency.get(n.id)?.size === 0)
      .map(n => n.id);

    const maxDepth = Math.max(...Array.from(this.nodes.values()).map(n => n.depth), 0);

    // Update nodes with critical path info
    for (const id of criticalPath) {
      const node = this.nodes.get(id);
      if (node) {
        node.task.inputs['__criticalPath'] = true;
      }
    }

    return {
      nodes: this.nodes,
      rootNodes,
      leafNodes,
      maxDepth,
      isValid: cycles.length === 0,
      cycles,
    };
  }

  /**
   * Serialize graph for visualization
   */
  serializeForVisualization(): {
    nodes: Array<{ id: string; label: string; type: string; depth: number; status: string; progress: number }>;
    edges: Array<{ from: string; to: string }>;
    layers: string[][];
    criticalPath: string[];
  } {
    const nodes = Array.from(this.nodes.values()).map(n => ({
      id: n.id,
      label: n.task.title,
      type: n.task.agentType,
      depth: n.depth,
      status: n.task.status,
      progress: n.task.progress,
    }));

    const edges: Array<{ from: string; to: string }> = [];
    for (const [id, deps] of this.adjacencyList) {
      for (const dep of deps) {
        edges.push({ from: dep, to: id });
      }
    }

    return {
      nodes,
      edges,
      layers: this.getExecutionLayers(),
      criticalPath: this.getCriticalPath(),
    };
  }
}

/**
 * Utility to validate task dependencies before building graph
 */
export function validateTaskDependencies(tasks: AgentTask[]): {
  valid: boolean;
  errors: string[];
  warnings: string[];
} {
  const errors: string[] = [];
  const warnings: string[] = [];
  const taskIds = new Set(tasks.map(t => t.id));

  for (const task of tasks) {
    // Check for self-dependency
    if (task.dependsOn.includes(task.id)) {
      errors.push(`Task "${task.title}" (${task.id}) depends on itself`);
    }

    // Check for missing dependencies
    for (const dep of task.dependsOn) {
      if (!taskIds.has(dep)) {
        errors.push(`Task "${task.title}" depends on missing task: ${dep}`);
      }
    }

    // Check for duplicate IDs
    const duplicates = tasks.filter(t => t.id === task.id);
    if (duplicates.length > 1) {
      errors.push(`Duplicate task ID: ${task.id}`);
    }
  }

  // Check for orphaned tasks (no connections)
  const connectedTasks = new Set<string>();
  for (const task of tasks) {
    connectedTasks.add(task.id);
    for (const dep of task.dependsOn) {
      connectedTasks.add(dep);
    }
  }

  for (const task of tasks) {
    const isReferenced = tasks.some(t => t.dependsOn.includes(task.id));
    if (task.dependsOn.length === 0 && !isReferenced && tasks.length > 1) {
      warnings.push(`Task "${task.title}" is isolated (no dependencies, no dependents)`);
    }
  }

  return { valid: errors.length === 0, errors, warnings };
}